import type { FastFeedDailyRow } from "@/api/types";
import { Panel } from "@/components/ui";
import { useChartColors } from "@/lib/theme";
import { useECharts } from "@/lib/use-echarts";
import type { EChartsOption } from "echarts";
import { useMemo } from "react";

function ChartInner({
  rows,
  cc,
}: { rows: FastFeedDailyRow[]; cc: ReturnType<typeof useChartColors> }) {
  const option = useMemo<EChartsOption>(
    () => ({
      grid: { left: 52, right: 16, top: 32, bottom: 32 },
      legend: {
        data: ["DAO Treasury (97%)", "Dev Guild (3%)"],
        textStyle: { color: cc.text, fontSize: 10 },
        top: 6,
      },
      xAxis: {
        type: "category",
        data: rows.map((r) => r.date),
        axisLabel: { fontSize: 10, color: cc.text },
        axisLine: { lineStyle: { color: cc.grid } },
        axisTick: { show: false },
        boundaryGap: false,
      },
      yAxis: {
        type: "value",
        axisLabel: { fontSize: 10, color: cc.text, formatter: (v: number) => `$${v.toFixed(0)}` },
        splitLine: { lineStyle: { color: cc.grid, type: "dashed" } },
      },
      tooltip: {
        trigger: "axis",
        formatter: (p: unknown) => {
          const params = p as Array<{ seriesName: string; name: string; value: number }>;
          return `<b>${params[0].name}</b><br/>${params.map((s) => `${s.seriesName}: $${(+s.value).toFixed(4)}`).join("<br/>")}`;
        },
      },
      series: [
        {
          name: "DAO Treasury (97%)",
          type: "line",
          data: rows.map((r) => +r.treasury_usdg.toFixed(4)),
          step: "end",
          symbol: rows.length <= 5 ? "circle" : "none",
          symbolSize: 5,
          lineStyle: { color: "#10b981", width: 2 },
          itemStyle: { color: "#10b981" },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(16,185,129,0.3)" },
                { offset: 1, color: "rgba(16,185,129,0.02)" },
              ],
            },
          },
        },
        {
          name: "Dev Guild (3%)",
          type: "line",
          data: rows.map((r) => +r.guild_usdg.toFixed(4)),
          step: "end",
          symbol: rows.length <= 5 ? "circle" : "none",
          symbolSize: 5,
          lineStyle: { color: "#f97316", width: 2 },
          itemStyle: { color: "#f97316" },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(249,115,22,0.25)" },
                { offset: 1, color: "rgba(249,115,22,0.02)" },
              ],
            },
          },
        },
      ],
    }),
    [rows, cc],
  );

  const ref = useECharts(option, [option]);
  return <div ref={ref} style={{ height: 220 }} />;
}

interface Props {
  rows: FastFeedDailyRow[];
  loading?: boolean;
}

export function FastFeedCumulativeBreakdownChart({ rows, loading = false }: Props) {
  const cc = useChartColors();
  return (
    <Panel title="PROTOCOL EARNINGS (DAILY)">
      {loading ? (
        <div className="skeleton" style={{ height: 220, borderRadius: "var(--radius-panel)" }} />
      ) : rows.length === 0 ? (
        <div
          style={{
            height: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--color-text-muted)",
            fontSize: 12,
          }}
        >
          No data in this range
        </div>
      ) : (
        <ChartInner rows={rows} cc={cc} />
      )}
    </Panel>
  );
}
